import type { CoreAPIConfig } from '@guardian/ab-core';
import { AB } from '@guardian/ab-core';
import { getCookie, isUndefined, log } from '@guardian/libs';
import { useEffect, useMemo, useState } from 'react';
import { getOphan } from '../client/ophan/ophan';
import { tests } from '../experiments/ab-tests';
import { runnableTestsToParticipations } from '../experiments/lib/ab-participations';
import { BetaABTests } from '../experiments/lib/beta-ab-tests';
import { getForcedParticipationsFromUrl } from '../lib/getAbUrlHash';
import { isServer } from '../lib/isServer';
import { setABTests, setBetaABTests } from '../lib/useAB';
import type { ABTestSwitches } from '../model/enhance-switches';
import type { ServerSideTests } from '../types/config';
import { useConfig } from './ConfigContext';

type Props = {
	abTestSwitches: ABTestSwitches;
	forcedTestVariants?: CoreAPIConfig['forcedTestVariants'];
	isDev: boolean;
	pageIsSensitive: CoreAPIConfig['pageIsSensitive'];
	serverSideTests: ServerSideTests;
	serverSideABTests: Record<string, string>;
};

const mvtMinValue = 1;
const mvtMaxValue = 1_000_000;

/** Parse a valid MVT ID between 1 and 1,000,000 or undefined if it fails */
const parseMvtId = (id: string | null): number | undefined => {
	if (!id) return; // null or empty string
	const number = Number(id);
	if (Number.isNaN(number)) return;
	if (number < mvtMinValue) return;
	if (number > mvtMaxValue) return;
	return number;
};

const getMvtId = () =>
	parseMvtId(
		getCookie({
			name: 'GU_mvt_id',
			shouldMemoize: true,
		}),
	);

/** Check if there is a local override */
const getLocalMvtId = () =>
	parseMvtId(
		getCookie({
			name: 'GU_mvt_id_local',
			shouldMemoize: true,
		}),
	);

const errorReporter = (e: unknown) =>
	window.guardian.modules.sentry.reportError(
		e instanceof Error ? e : Error(String(e)),
		'ab-tests',
	);

/**
 * Initialises the values of `useAB` and sends relevant Ophan events.
 *
 * ## Why does this need to be an Island?
 *
 * All this logic is client-side.
 *
 * ---
 *
 * Does not render **anything**.
 */
export const SetABTests = ({
	isDev,
	pageIsSensitive,
	abTestSwitches,
	forcedTestVariants,
	serverSideTests,
	serverSideABTests,
}: Props) => {
	const { renderingTarget } = useConfig();
	const [ophan, setOphan] = useState<Awaited<ReturnType<typeof getOphan>>>();

	useEffect(() => {
		getOphan(renderingTarget)
			.then(setOphan)
			.catch((e) => {
				console.log(
					`There was an error retrieving the ophan window object`,
					e,
				);
			});
	}, [renderingTarget]);

	const betaAb = useMemo(() => {
		const betaAB = new BetaABTests(
			isServer
				? {
						serverSideABTests,
						isServer: true,
				  }
				: { isServer: false },
		);
		setBetaABTests(betaAB);
		return betaAB;
	}, [serverSideABTests]);

	useEffect(() => {
		if (!ophan) return;

		const mvtId = isDev ? getLocalMvtId() ?? getMvtId() : getMvtId();

		if (isUndefined(mvtId)) {
			console.error(
				'There is no MVT ID set, see SetABTests.importable.tsx',
			);
		}

		const allForcedTestVariants = {
			...forcedTestVariants,
			...getForcedParticipationsFromUrl(window.location.hash),
		};

		const ab = new AB({
			mvtId: mvtId ?? -1,
			mvtMaxValue,
			pageIsSensitive,
			abTestSwitches,
			arrayOfTestObjects: tests,
			forcedTestVariants: allForcedTestVariants,
			ophanRecord: ophan.record,
			serverSideTests,
			errorReporter,
		});
		const allRunnableTests = ab.allRunnableTests(tests);
		const participations = runnableTestsToParticipations(allRunnableTests);

		setABTests({
			api: ab,
			participations,
		});

		ab.trackABTests(allRunnableTests);
		ab.registerImpressionEvents(allRunnableTests);
		ab.registerCompleteEvents(allRunnableTests);

		betaAb.trackABTests(ophan.record, errorReporter);

		log('dotcom', 'AB tests initialised');
	}, [
		abTestSwitches,
		forcedTestVariants,
		isDev,
		pageIsSensitive,
		ophan,
		serverSideTests,
		betaAb,
	]);

	// we don’t render anything
	return null;
};
